import { useEffect, useState } from 'react';

import { useAuth } from '@opendoor/auth-fe/context';
import {
  getExperimentAssignment,
  recordExperimentImpression,
} from 'components/api/zeusExperiments';
import type { ExperimentAssignment } from 'components/api/zeusExperiments';
import { useIsBot } from 'helpers/IsBotContext';
import { useObservability } from 'helpers/observability';
import { z } from 'zod';

export const CERTAINTY_PANEL_EXPERIMENT_KEY = 'resale-26-08-pdp-certainty-panel';

const TREATMENT = 'treatment';

// Scoped to the identity that produced the value. sessionStorage survives an
// in-tab sign-in, so a shared key would keep serving an anonymously bucketed
// variant while impressions started reporting the customer's id.
const cacheKey = (customerUUID?: string) =>
  `${CERTAINTY_PANEL_EXPERIMENT_KEY}:${customerUUID ?? 'anonymous'}`;

const cachedAssignmentSchema = z.object({
  value: z.string(),
  // Older entries predate the server-provided index; treat them as unbucketed
  // rather than discarding an otherwise usable assignment.
  variationId: z.number().nullable().catch(null),
});

const readCachedAssignment = (customerUUID?: string): ExperimentAssignment | null => {
  try {
    const raw = sessionStorage.getItem(cacheKey(customerUUID));
    if (!raw) {
      return null;
    }
    const parsed = cachedAssignmentSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
};

const writeCachedAssignment = (
  customerUUID: string | undefined,
  assignment: ExperimentAssignment,
) => {
  try {
    sessionStorage.setItem(cacheKey(customerUUID), JSON.stringify(assignment));
  } catch {
    // Per-session convenience only; assignment still comes from the server.
  }
};

type ExperimentState = {
  // Identity the arm was bucketed for. An arm is only served back to the
  // identity that produced it, so a visitor who signs in with the PDP open
  // never reports the previous arm against their customer id.
  identity: string;
  // Null until the assignment resolves; the SSR'd surfaces stay hidden so
  // hydration matches the server HTML.
  variant: string | null;
  variationId: number | null;
  // A failed or unbucketed assignment renders control but is not a real
  // assignment: it is never cached and never reports an impression, so those
  // users do not contaminate the exposure log.
  isFallback: boolean;
};

export type CertaintyPanelExperimentState = {
  // Identity the arm was bucketed for. The exposure latch keys on it so a
  // mid-page sign-in, which re-buckets under the customer id, records an
  // exposure for the new unit instead of reporting the anonymous one.
  identity: string;
  variant: string | null;
  // True when the variant is the local fail-closed control rather than a real
  // assignment; such users must stay out of every exposure-labeled stream.
  isFallback: boolean;
  // True only for experiment-backed assignments (server variationId present);
  // a feature default or force rule renders but never logs exposure.
  isExperimentAssignment: boolean;
  isTreatment: boolean;
  trackImpression: () => void;
};

type UseCertaintyPanelExperimentOptions = {
  /** Whether this listing can show the panel at all. An ineligible listing is never bucketed. */
  enabled: boolean;
};

export const useCertaintyPanelExperiment = ({
  enabled,
}: UseCertaintyPanelExperimentOptions): CertaintyPanelExperimentState => {
  const { user, authentication } = useAuth();
  const isBot = useIsBot();
  const { trackJourneyEvent } = useObservability();
  // Allowlisted rather than "not authenticating": the context's fourth state,
  // `error`, means the session could not be determined, so a signed-in viewer
  // may be behind it. Bucketing there records the exposure against an anonymous
  // id that a recovering session would replace.
  const isAuthSettled =
    authentication.state === 'authenticated' || authentication.state === 'not_authenticated';
  const customerUUID = user?.customerUuid ?? undefined;
  const identity = customerUUID ?? 'anonymous';
  const [state, setState] = useState<ExperimentState>({
    identity,
    variant: null,
    variationId: null,
    isFallback: false,
  });

  useEffect(() => {
    let cancelled = false;

    // Matches useListingExperiment: an ineligible listing is never bucketed, so
    // no sticky GrowthBook assignment is written for a viewer who could never
    // be exposed. Eligibility is arm-independent, so this cannot bias the split.
    if (!enabled || isBot) {
      return;
    }

    // Evaluating before the session settles buckets the visitor under the
    // anonymous cache key and re-evaluates under their customer id once it
    // lands, which costs a second assignment and can expose one arm while
    // serving the other.
    if (!isAuthSettled) {
      return;
    }

    const resolve = async (): Promise<ExperimentState> => {
      const cached = readCachedAssignment(customerUUID);
      if (cached) {
        return {
          identity,
          variant: cached.value,
          variationId: cached.variationId,
          isFallback: false,
        };
      }

      const assignment = await getExperimentAssignment(
        CERTAINTY_PANEL_EXPERIMENT_KEY,
        customerUUID,
      );
      // Undefined means the request failed; a null value means the server
      // answered "not bucketed". Neither is a real assignment.
      if (assignment?.value == null) {
        return { identity, variant: 'control', variationId: null, isFallback: true };
      }

      // A value served without a variation index is a feature default or a
      // force rule, not a bucketing. Caching it would keep serving an arm that
      // can never be exposed for the rest of the session, including after the
      // experiment is ramped on.
      if (assignment.variationId != null) {
        writeCachedAssignment(customerUUID, assignment);
      }
      return {
        identity,
        variant: assignment.value,
        variationId: assignment.variationId,
        isFallback: false,
      };
    };

    void resolve().then((resolved) => {
      if (!cancelled) {
        setState(resolved);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [customerUUID, enabled, identity, isBot, isAuthSettled]);

  // Not enrolled rather than fail-closed control: this listing shows no panel
  // in either arm, so there is no arm to report.
  if (!enabled) {
    return {
      identity,
      variant: null,
      isFallback: false,
      isExperimentAssignment: false,
      isTreatment: false,
      trackImpression: () => {},
    };
  }

  // Crawlers are excluded from the experiment entirely. initObservability
  // withholds the Journey client for them, so a bucketed bot would reach the
  // Zeus impression mutation and log an exposure the Journey stream never
  // records, splitting the two logs against each other.
  if (isBot) {
    return {
      identity,
      variant: 'control',
      isFallback: true,
      isExperimentAssignment: false,
      isTreatment: false,
      trackImpression: () => {},
    };
  }

  // An arm resolved for a previous identity is withheld rather than served,
  // so the window between a sign-in and the replacement assignment renders as
  // unresolved instead of exposing one arm under another identity.
  const resolved: ExperimentState =
    state.identity === identity
      ? state
      : { identity, variant: null, variationId: null, isFallback: false };

  return {
    identity,
    variant: resolved.variant,
    isFallback: resolved.isFallback,
    isExperimentAssignment:
      !resolved.isFallback && resolved.variant != null && resolved.variationId != null,
    isTreatment: resolved.variant === TREATMENT,
    trackImpression: () => {
      // A null variationId means the value came from a feature default or
      // force rule, not experiment bucketing. Those renders must stay out of
      // both exposure streams or a paused or ramping experiment would log
      // phantom control exposures.
      if (resolved.variant == null || resolved.isFallback || resolved.variationId == null) {
        return;
      }
      void recordExperimentImpression(
        CERTAINTY_PANEL_EXPERIMENT_KEY,
        { variant: resolved.variant, variationId: resolved.variationId },
        customerUUID,
      );
      // GrowthBook's warehouse assignment query joins exposures on
      // variation_id and drops rows where it is null, so the journey
      // impression carries the server-assigned index alongside the label.
      trackJourneyEvent('experimentation', 'impression', {
        experimentName: CERTAINTY_PANEL_EXPERIMENT_KEY,
        variant: resolved.variant,
        variationId: resolved.variationId,
      });
    },
  };
};
